/**
 * Direct Message Content Type
 *
 * Encrypted messages between two users. Only sender and recipient
 * can decrypt the message content.
 *
 * Encryption: AES-256-GCM with key derived from both users' public keys
 */

import type { DAGObject, ObjectType } from '../dag/types.js';
import { sha512 } from '@noble/hashes/sha512';
import { base58 } from '../identity/base58.js';

/**
 * Message content (stored encrypted in DAG)
 */
export interface MessageContent {
  /** Recipient's public key (base58) */
  recipient: string;
  /** Encrypted message payload (base64) */
  encrypted: string;
  /** Initialization vector (base64) */
  iv: string;
  /** Optional: hash of message being replied to */
  replyTo?: string;
}

/**
 * Decrypted message payload
 */
export interface DecryptedMessage {
  /** Message text */
  text: string;
  /** Optional attachment hashes (clipboard entries) */
  attachments?: string[];
  /** Timestamp when composed (may differ from DAG timestamp) */
  composedAt?: number;
}

/**
 * Type guard for message objects
 */
export function isMessage(obj: DAGObject): obj is DAGObject<MessageContent> {
  return obj.type === 'message';
}

/**
 * Derive a shared encryption key from two public keys
 *
 * Uses SHA-512 hash of both keys (in deterministic order) as key material.
 * This ensures both parties derive the same key.
 */
export function deriveSharedKey(pubkey1: string, pubkey2: string): Uint8Array {
  // Sort keys to ensure deterministic order
  const [first, second] = [pubkey1, pubkey2].sort();

  // Decode keys
  const key1Bytes = base58.decode(first);
  const key2Bytes = base58.decode(second);

  // Combine and hash
  const combined = new Uint8Array(key1Bytes.length + key2Bytes.length);
  combined.set(key1Bytes, 0);
  combined.set(key2Bytes, key1Bytes.length);

  // Use SHA-512 and take first 32 bytes as AES-256 key
  const hash = sha512(combined);
  return hash.slice(0, 32);
}

/**
 * Encrypt a message for a recipient
 */
export async function encryptMessage(
  senderPubkey: string,
  recipientPubkey: string,
  payload: DecryptedMessage
): Promise<{ encrypted: string; iv: string }> {
  const keyMaterial = deriveSharedKey(senderPubkey, recipientPubkey);

  // Import key for AES-GCM
  const key = await crypto.subtle.importKey(
    'raw',
    keyMaterial.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt payload
  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    payloadBytes
  );

  return {
    encrypted: arrayBufferToBase64(new Uint8Array(ciphertext)),
    iv: arrayBufferToBase64(iv),
  };
}

/**
 * Decrypt a message
 */
export async function decryptMessage(
  myPubkey: string,
  otherPubkey: string,
  encrypted: string,
  iv: string
): Promise<DecryptedMessage> {
  const keyMaterial = deriveSharedKey(myPubkey, otherPubkey);

  // Import key for AES-GCM
  const key = await crypto.subtle.importKey(
    'raw',
    keyMaterial.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  // Decode encrypted data and IV
  const ciphertextBytes = base64ToArrayBuffer(encrypted);
  const ivBytes = base64ToArrayBuffer(iv);

  // Decrypt
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
    key,
    ciphertextBytes.buffer as ArrayBuffer
  );

  const json = new TextDecoder().decode(plaintext);
  return JSON.parse(json);
}

/**
 * Create an encrypted message content object
 */
export async function createMessageContent(
  senderPubkey: string,
  recipientPubkey: string,
  text: string,
  options?: {
    attachments?: string[];
    replyTo?: string;
  }
): Promise<{ type: ObjectType; content: MessageContent }> {
  const payload: DecryptedMessage = {
    text,
    attachments: options?.attachments,
    composedAt: Date.now(),
  };

  const { encrypted, iv } = await encryptMessage(senderPubkey, recipientPubkey, payload);

  return {
    type: 'message' as ObjectType,
    content: {
      recipient: recipientPubkey,
      encrypted,
      iv,
      replyTo: options?.replyTo,
    },
  };
}

/**
 * Get the other party in a message conversation
 */
export function getOtherParty(message: DAGObject<MessageContent>, myPubkey: string): string {
  if (message.creator === myPubkey) {
    return message.content.recipient;
  }
  return message.creator;
}

/**
 * Check if a message is part of a conversation between two users
 */
export function isConversationMessage(
  message: DAGObject<MessageContent>,
  user1: string,
  user2: string
): boolean {
  const sender = message.creator;
  const recipient = message.content.recipient;

  return (
    (sender === user1 && recipient === user2) ||
    (sender === user2 && recipient === user1)
  );
}

// ============================================================================
// Helpers
// ============================================================================

function arrayBufferToBase64(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
